import React, { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { LogOut, Users } from 'lucide-react'
import { useKeycloak } from '@react-keycloak/web'
import { jwtDecode } from "jwt-decode";

import { PARCEL_QUERY } from 'src/graphql/queries/parcel.queries'
import { AppAuthProvider } from 'src/middleware'
import { relativeTime } from 'src/utils/time';
import { useParcelStore } from 'src/store/parcel';
import { removeDefaultRole } from 'src/utils/common';
import { 
  connect as connectTodoSync, 
  disconnect as disconnectTodoSync, 
  subscribeConnectionStatus, 
  getAllClients,
  wsProvider 
} from 'src/store/todo';
import { useTransactor } from 'src/hooks/useTranstor';

const Settings: React.FC = () => {
  const { authProvider } = AppAuthProvider()
  const { keycloak } = useKeycloak();
  const { token } = keycloak

  const {initiateRoomFn } = useTransactor()
  const setParcelState= useParcelStore((state)=>state.setParcelState)
  const room = useParcelStore((state) => state.roomData)
  const activeParcel= useParcelStore((state)=>state.parcel)
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected'>('disconnected')
  const [isOnline, setIsOnline] = useState(true)
  const [clients, setClients] = useState<any[]>([])

  const decoded: UserSessionJWT | null = token ? jwtDecode(token) : null;

  const { data,loading} = useQuery(PARCEL_QUERY)

  const intials = decoded?.name?.split(' ')

  const onParcelChange = async(e: React.ChangeEvent<HTMLSelectElement>) => {
    setParcelState(e.target.value)
    await initiateRoomFn({payload:{parcelId:e.target.value, version:'1', date:new Date().toISOString()}})
  }

  useEffect(() => {
    const unsubscribe = subscribeConnectionStatus(setConnectionStatus)
    return unsubscribe
  }, [])

  useEffect(() => {
    if (!wsProvider?.awareness) return

    const updateClients = () => {
      setClients(getAllClients())
    }

    updateClients()
    wsProvider.awareness.on('change', updateClients)

    return () => {
      if (wsProvider?.awareness) {
        wsProvider.awareness.off('change', updateClients)
      }
    }
  }, [connectionStatus])

  // Handle online/offline toggle
  const handleToggleOnline = () => {
    if (isOnline) {
      disconnectTodoSync()
      setIsOnline(false)
    } else {
      connectTodoSync({room:room?.roomId, user: decoded  as UserSessionJWT || null})
      setIsOnline(true)
    }
  }

  // Format last seen time
  const formatLastSeen = (heartbeat: number): string => {
    if (!heartbeat) return 'Never'
    const now = Date.now()
    const diff = now - heartbeat
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    
    if (seconds < 30) return 'Just now'
    if (seconds < 60) return `${seconds}s ago`
    if (minutes < 60) return `${minutes}m ago`
    return 'Offline'
  }

  return (
    <div className='pb-44'>
      <div className='flex flex-col gap-2 items-center justify-center py-7 border-b border-neutral-200'>
        <div className='w-14 h-14 p-1 rounded-full flex items-center justify-center bg-blue-800 text-white'>
          <p className='text-2xl uppercase'>{`${intials?.[0]?.charAt(0)}${intials?.[1].charAt(0)}`}</p>
        </div>
        <div className='text-center'>
          <p className='text-xl font-medium capitalize'> {decoded?.name} </p>
          <p>{decoded?.email} </p>
        </div>
      </div>

      {/* SyncedStore Connection Status */}
      <div className='px-3 pt-3 mb-3'>
        <div className='border border-neutral-200 rounded-md p-3 bg-white'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <p className='font-semibold text-sm'>Sync Service</p>
              <div className='flex items-center gap-1'>
                <div 
                  className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ boxShadow: connectionStatus === 'connected' ? '0 0 4px rgba(34, 197, 94, 0.5)' : '0 0 4px rgba(239, 68, 68, 0.5)' }}
                />
                <span className='text-xs text-gray-600'>
                  {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
            {/* Toggle Switch */}
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={isOnline}
                onChange={handleToggleOnline}
                className='sr-only peer'
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Connected Clients */}
      {connectionStatus === 'connected' && clients.length > 0 && (
        <div className='px-3 mb-3'>
          <div className='border border-neutral-200 rounded-md p-3 bg-white'>
            <div className='flex items-center gap-2 mb-3'>
              <Users className='w-4 h-4' />
              <p className='font-semibold text-sm'>Connected Clients ({clients.length})</p>
            </div>
            <div className='space-y-2 max-h-60 overflow-y-auto'>
              {clients.map((client) => (
                <div 
                  key={client.id}
                  className={`p-2 rounded border ${client.isLocal ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
                >
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <span className='text-lg'>{client.emoji}</span>
                      <div>
                        <p className='text-sm font-medium capitalize'>
                          {client.fruit} {client.isLocal && '(You)'}
                        </p>
                        <p className='text-xs text-gray-600'>
                          ID: {client.id} • {client.ip}
                        </p>
                      </div>
                    </div>
                    <div className='text-right'>
                      <div className='flex items-center gap-1'>
                        <div 
                          className={`w-1.5 h-1.5 rounded-full ${
                            client.heartbeat && Date.now() - client.heartbeat < 30000 
                              ? 'bg-green-500' 
                              : 'bg-gray-400'
                          }`}
                        />
                        <span className='text-xs text-gray-500'>
                          {formatLastSeen(client.heartbeat)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className='px-3 flex flex-col gap-2'>
        <div>
          <p className='font-semibold text-sm py-1'>Parcel</p>
          <select  value={activeParcel} onChange={onParcelChange} className='text-sm border border-neutral-100 w-full focus:outline-none focus:ring-0 focus:border-neutral-100 px-1 h-11 rounded-md'>
          <option value="">{loading?'loading...':'Select Parcel'}</option>
            {data?.parcels?.map((parcel: Record<string, string>) => (
              <option key={parcel.id} value={parcel.id}>{parcel?.parcel_name}</option>))}
          </select>
        </div>
        <div>
          <p className='font-semibold text-sm'>Username</p>
          <div className='border border-neutral-100 text-neutral-600  p-2 h-10 capitalize rounded-md bg-white'>
            {decoded?.preferred_username}
          </div>
        </div>
        <div>
          <p className='font-semibold text-sm'>Email Verified</p>
          <div className={`border border-neutral-100 p-2 h-10 ${decoded?.email_verified ? 'text-green-700' : 'text-red-700'} rounded-md bg-white`}>
            {decoded?.email_verified ? 'Verified' : 'Unverified'}
          </div>
        </div>
        <div>
          <p className='font-semibold text-sm'>Role</p>
          <ul className='border space-x-3 border-neutral-100 p-2 m-h-10 flex gap-2 flex-wrap rounded-md bg-white'>
           {removeDefaultRole(decoded?.realm_access?.roles || [])?.map((role:string,idx:number)=>(
                <li className='text-neutral-600' key={idx}>{role}</li>
             ))}
          </ul>
        </div>
        <div>
          <p className='font-semibold text-sm'>Permisson</p>
          <ul className='border space-x-2 border-neutral-100 p-2 m-h-10 flex gap-2 flex-wrap rounded-md bg-white'>
             {decoded?.resource_access?.account?.roles?.map((role,idx)=>(
                <li key={idx} className='text-neutral-600'>{role} </li>
             ))}
          </ul>
        </div>
        <div>
          <p className='font-semibold text-sm'>Last LoggedIn</p>
          <div className='border border-neutral-100 p-2 text-neutral-600 h-10 rounded-md bg-white'>
            {decoded?.auth_time ? relativeTime(decoded?.auth_time) : ''}
          </div>
        </div>
        <div>
          <p className='font-semibold text-sm'>Cached Version</p>
          <div className='border border-neutral-100 p-2 text-neutral-600 h-10 rounded-md bg-white'>
            Oct-31 2025 18:05:00 v0.9.006 eventuallyZ
          </div>
        </div>
        <button type="button" className='h-8 rounded-md mt-5 flex gap-1 justify-center items-center text-sm w-24 bg-red-700 text-white hover:bg-red-800'
          onClick={() => authProvider.logout()}>
          <LogOut className='w-4' />
          Logout
        </button>
      </div>
    </div>
  )
}
export default Settings
